/**
 * Level 16: Enchanted Forest
 * 
 * A magical forest with bioluminescent plants and fireflies.
 * Introduces Flying Enemies in a lush, vertical environment.
 * Features wind zones simulating forest drafts and portal tree trunks.
 * 
 * Design philosophy: Organic flowing layout with gentle difficulty curve
 * for the new enemy type introduction.
 * 
 * @module configs/levels/Level16_EnchantedForest
 */

/** @type {import('../LevelTypes').LevelConfig} */
const Level16_EnchantedForest = {
    id: 16,
    name: 'Enchanted Forest',
    subtitle: 'Whispers of the Wild',
    description: 'Navigate a magical forest filled with flying creatures and ancient portals.',

    worldWidth: 5200,
    worldHeight: 1800,
    playerStart: { x: 120, y: 1550 },
    gravity: 0.95,

    theme: {
        bgGradient: ['#0a1f0a', '#1a3a2a'],
        platformColor: 0x2d5a27,
        accentColor: 0x66ff99,
        particleColor: 0x88ffaa,
        ambient: 'forest'
    },

    platforms: [
        // Root entrance - wide landing area
        { x: 0, y: 1650, w: 500, h: 60 },
        { x: 350, y: 1500, w: 120, h: 20 },
        { x: 550, y: 1350, w: 160, h: 25 },

        // Mushroom stepping stones
        { x: 800, y: 1450, w: 100, h: 20 },
        { x: 1000, y: 1300, w: 130, h: 22 },
        { x: 1200, y: 1150, w: 100, h: 20 },

        // Ancient tree trunk bridge
        { x: 1400, y: 1250, w: 400, h: 40 },
        { x: 1650, y: 1100, w: 150, h: 22 },

        // Canopy platforms  
        { x: 1900, y: 950, w: 200, h: 30 },
        { x: 2200, y: 800, w: 160, h: 25 },
        { x: 2450, y: 950, w: 180, h: 25 },

        // Clearing
        { x: 2700, y: 1100, w: 350, h: 45 },
        { x: 3100, y: 1250, w: 200, h: 30 },

        // Hollow tree descent
        { x: 3400, y: 1100, w: 150, h: 25 },
        { x: 3300, y: 950, w: 130, h: 22 },
        { x: 3500, y: 800, w: 160, h: 25 },

        // Treetop finale
        { x: 3750, y: 1000, w: 250, h: 35 },
        { x: 4050, y: 850, w: 180, h: 25 },
        { x: 4350, y: 700, w: 200, h: 30 },
        { x: 4600, y: 900, w: 300, h: 50 },
        { x: 4950, y: 1100, w: 250, h: 50 }
    ],

    movingPlatforms: [
        // Floating lily pads
        { x: 700, y: 1520, w: 90, h: 18, moveY: 100, speed: 0.002 },
        { x: 1850, y: 1020, w: 110, h: 18, moveX: 120, speed: 0.0025 },
        { x: 3200, y: 1180, w: 100, h: 18, moveY: 130, speed: 0.003 },
        { x: 4200, y: 770, w: 100, h: 18, moveX: 100, speed: 0.002 }
    ],

    spikes: [
        // Thorny vines
        { x: 1450, y: 1250 },
        { x: 1500, y: 1250 },
        { x: 2750, y: 1100 },
        { x: 2800, y: 1100 },
        { x: 4650, y: 900 },
        { x: 4700, y: 900 }
    ],

    stars: [
        { x: 550, y: 1250 },
        { x: 1200, y: 1050 },
        { x: 2200, y: 700 },
        { x: 3300, y: 850 },
        { x: 4350, y: 600 },
        { x: 5050, y: 1000 }
    ],

    enemies: [
        // Ground patrols
        { x: 1500, y: 1200 },
        { x: 2800, y: 1050 },
        // Flying enemies (new!)
        { x: 900, y: 1200, type: 'flying', options: { amplitude: 60, speed: 1.2, walkDist: 120 } },
        { x: 2000, y: 750, type: 'flying', options: { amplitude: 70, speed: 1.4, walkDist: 130 } },
        { x: 3600, y: 700, type: 'flying', options: { amplitude: 50, speed: 1.6, walkDist: 100 } },
        { x: 4400, y: 600, type: 'flying', options: { amplitude: 80, speed: 1.0, walkDist: 140 } }
    ],

    door: { x: 5100, y: 950, w: 50, h: 300 },
    button: { x: 4950, y: 1095 },
    box: { x: 4100, y: 800, floating: true },
    goal: { x: 5150, y: 1000 },

    windZones: [
        // Forest updraft through canopy
        { x: 1850, y: 800, w: 300, h: 500, forceX: 0, forceY: -1.5 },
        // Gentle breeze
        { x: 3700, y: 700, w: 250, h: 400, forceX: 1.0, forceY: -0.5 }
    ],

    portals: [
        // Ancient tree portal
        { x1: 500, y1: 1600, x2: 2700, y2: 1050, color: 0x66ff99 }
    ],

    trampolines: [
        { x: 1350, y: 1250, w: 70 },
        { x: 3050, y: 1250, w: 70 },
        { x: 4550, y: 900, w: 70 }
    ],

    speedrunTargets: { gold: 55, silver: 75, bronze: 100 }
};

export default Level16_EnchantedForest;
